/**
 * Filling the sidebar from Canvas, and what to say when that fails.
 *
 * Kept apart from screens.js so the rules can be tested without a DOM. The
 * rules are the interesting part; the rendering is not.
 *
 * The distinction that matters: an expired Canvas token is not a retry. Fixing
 * it needs a browser somebody has signed into, on a desktop that may be
 * switched off, so a spinner or a retry button here would be theatre. Every
 * other failure genuinely is worth trying again.
 */
import { courses as fetchCourses, describeCanvasError } from './canvas.js';
import { fromCanvasCourses, replaceClasses } from './classes.js';

/**
 * @returns {{ok: boolean, count: number, message: string, canRetry: boolean}}
 *
 * Never throws. The caller is a sign-in path, and a Canvas outage must not
 * cost somebody the ability to upload a recording -- which is the thing this
 * page was built for and the thing that still works when Canvas is down.
 */
export async function loadClasses(config, session, deps = {}) {
  const get = deps.fetchCourses ?? fetchCourses;

  let courses;
  try {
    courses = await get(config, session, deps.fetchImpl);
  } catch (error) {
    replaceClasses([]);
    const shown = describeCanvasError(error);
    return { ok: false, count: 0, message: shown.message, canRetry: shown.canRetry };
  }

  const classes = replaceClasses(fromCanvasCourses(courses));
  if (classes.length === 0) {
    // Enrolled in nothing the API can see. Between terms this is the ordinary
    // answer, so it is not phrased as a failure.
    return {
      ok: true,
      count: 0,
      message: 'No active courses in Canvas right now.',
      canRetry: true,
    };
  }
  return { ok: true, count: classes.length, message: '', canRetry: false };
}
